import { getMessaging, isSupported } from 'firebase/messaging';
import { initApp } from '../libs/state';
import { useLocation } from 'react-router-dom';
import AppRouter from './AppRouter';
import Context from '../provider';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import config from '../config';

export default function Main() {
  const { init, dispatch, lang, program } = useContext(Context);
  const location = useLocation();
  const [loadMainError, setLoadMainError] = useState(null);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [
    location.search
  ]);

  useEffect(() => {

    responsive();

    document.body.onresize = responsive;

  }, []);

  const messagingPromise = async () => {
    try {
      const supported = await isSupported();
      if (supported) {
        getMessaging();
        dispatch({ type: 'SET_SUPPORT_FIREBASE', payload: true });
      } else {
        dispatch({ type: 'SET_SUPPORT_FIREBASE', payload: false });
      }
    } catch (error) {
      dispatch({ type: 'SET_SUPPORT_FIREBASE', payload: false });
    }
  };

  const responsive = () => {
    let standardWidth = 375;
    let width = document.body.clientWidth;
    if (width >= 691) {
      standardWidth = 1320;
      dispatch({ type: 'SWITCH_SHOW_TYPE', payload: false });
    } else {
      standardWidth = 375;
      dispatch({ type: 'SWITCH_SHOW_TYPE', payload: true });
    }
    const fontsize = (Math.min(width, 1320) / standardWidth) * 16;
    document.documentElement.style.fontSize = fontsize + 'px';
  };

  /**
   * Initial app data
   */
  const loadData = async () => {
    try {
      console.log('loadData', 'initApp');
      const urlToken = searchParams.get('wallet_token');
      if (urlToken) localStorage.setItem('walletAccessQuery', location.search);
      await initApp({ dispatch, urlToken });
    } catch (error) {
      console.log('loadData', error);
      console.log('loadData message', error.message);
      switch (error.message) {
        case 'AxiosError: Network Error':
          setLoadMainError('NEWWORK_ERROR');
          break;
        default:
          setLoadMainError('NO_PERMISSION');
          break;
      }
    }
  };

  /**
   * Set web title, theme color, manifest and apple-touch-icon
   * Ref: https://developers.google.com/web/fundamentals/web-app-manifest/
   * Ref: https://web.dev/articles/add-manifest?hl=zh-tw
   * Ref: https://developer.chrome.com/docs/lighthouse/pwa/apple-touch-icon?hl=zh-tw
   * Ref: https://stackoverflow.com/questions/52997333/how-to-create-dynamic-manifest-json-file-for-pwa-in-reactjs
   * @param {object} program
   * @param {string} lang
   * @returns
   */
  const setAppConfig = (program, lang) => {
    if (!program) return;

    const appName = program?.app_title?.[lang] ?? 'Member Center';
    document.title = appName;
    document.querySelector('meta[name="apple-mobile-web-app-title"]')
      .setAttribute('content', appName);
    if (program?.app_icon)
      document.querySelector('link#app-icon')
        .setAttribute('href', program.app_icon);
    if (program?.theme?.primaryColor)
      document.querySelector('meta[name="theme-color"]')
        .setAttribute('content', '#ffffff');

    const domain = `${window.location.protocol}//${window.location.host}${config.basename}`;
    const manifestJSON = {
      name: appName,
      short_name: appName,
      display: 'minimal-ui', // fullscreen, standalone, minimal-ui, browser
      start_url: `${domain}${localStorage.getItem('walletAccessQuery') ?? ''}`,
      orientation: 'portrait',
      icons: [
        {
          src: `${domain}icon-192.png`,
          sizes: '192x192',
          type: 'image/png'
        },
        {
          src: `${domain}icon-512.png`,
          sizes: '512x512',
          type: 'image/png'
        }
      ]
    };
    if (program?.theme?.primaryColor)
      manifestJSON.theme_color = program.theme.primaryColor;
    if (program?.theme?.backColor)
      manifestJSON.background_color = program.theme.backColor;
    if (program?.theme?.descriptionText?.[lang])
      manifestJSON.description = program.theme.descriptionText[lang];

    // Add manifest to head
    document.querySelector('#pwa-manifest')
      .setAttribute('href', `data:application/json;charset=utf-8,${encodeURIComponent(JSON.stringify(manifestJSON))}`);
  };

  useEffect(() => {
    if (!init) loadData();
  }, [init]);

  useEffect(() => {
    if (init && lang) setAppConfig(program, lang);
  }, [init, lang]);

  useEffect(() => {
    if (loadMainError !== null) {
      console.error('Main Load Error:', loadMainError);
      dispatch({ type: 'SET_ERROR', payload: loadMainError });
      setLoadMainError(null);
    }
  }, [loadMainError, dispatch]);

  return <AppRouter />;
}